import { combineReducers } from '@reduxjs/toolkit';
import { pick } from 'lodash';
import { persistReducer, type PersistConfig } from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/es/storage';
import storageSession from 'redux-persist/es/storage/session';
import type { AccountActionTypes } from './modules/accounts/actions';
import account, { AccountState } from './modules/accounts/reducer';
import type { AuthActionType } from './modules/auth/actions';
import auth, { AuthState } from './modules/auth/reducer';
import benefits from './modules/benefits/reducer';
import header from './modules/components/header';
import modals from './modules/components/modals';
import enterprise, { EnterpriseState } from './modules/enterprise/reducer';
import { OrganizationActionTypes } from './modules/enterprise/types';
// New file structure
import enterpriseSurvey from './modules/enterpriseSurvey';
import legs from './modules/legs/reducer';
import localSettings from './modules/localSettings/reducer';
import metrics from './modules/metrics/reducer';
import plans from './modules/plans/reducer';
import providers from './modules/providers/reducer';
import routes from './modules/routes/reducer';
import services from './modules/services/reducer';
import surveyBuilder from './modules/surveyBuilder/slice';
import UI from './modules/UI';
export const persistBlacklist: (keyof RootState)[] = ['enterpriseSurvey', 'enterprise', 'auth', 'account'];

// Persist a slice without its request-lifecycle flags: restoring them after a reload mid-request left the
// login spinner (`auth.loading`) or the full-page account error (`account.error`) stuck
const withoutTransientKeys = <S extends object,>(key: string, transientKeys: (keyof S & string)[]): PersistConfig<Partial<S>> => ({
  key,
  storage,
  // The blacklist only stops future writes; the reconciler also drops these keys from slices already in storage
  blacklist: transientKeys,
  stateReconciler: (inbound, original, reduced, config) => {
    // Stored transient values are replaced with the freshly reduced (initial) ones
    const persisted = {
      ...inbound,
      ...pick(reduced, transientKeys)
    };
    return autoMergeLevel1(persisted, original, reduced, config);
  }
});
export const authPersistConfig = withoutTransientKeys<AuthState>('auth', ['loading', 'loggingOut', 'error']);
const accountPersistConfig = withoutTransientKeys<AccountState>('account', ['isFetching', 'error']);
const surveyPersistConfig = {
  key: 'enterpriseSurvey',
  storage: storageSession
};
const enterprisePersistConfig = {
  key: 'enterprise',
  storage
};
const appReducer = combineReducers({
  modals,
  header,
  enterprise: persistReducer<Partial<EnterpriseState>, OrganizationActionTypes>(enterprisePersistConfig, enterprise),
  auth: persistReducer<Partial<AuthState>, AuthActionType>(authPersistConfig, auth),
  account: persistReducer<Partial<AccountState>, AccountActionTypes>(accountPersistConfig, account),
  localSettings,
  metrics,
  routes,
  legs,
  services,
  providers,
  plans,
  benefits,
  // We use any here because the reducer is not typed yet. This is older code that would take some time to transition to typescript
  enterpriseSurvey: persistReducer(surveyPersistConfig, enterpriseSurvey as any),
  surveyBuilder,
  UI
});
const rootReducer = (state: ReturnType<typeof appReducer>, action: AccountActionTypes | AuthActionType) => appReducer(state, action);
export type RootState = ReturnType<typeof appReducer>;
export default rootReducer;